import { FC, Fragment, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { DateTimeUtils } from '@servicetitan/datetime-utils';
import {
    Button,
    ButtonGroup,
    Divider,
    Eyebrow,
    Link,
    Modal,
    Stack,
    Table,
    TableCellProps,
    TableColumn,
    TableNoRecords,
    Tooltip,
} from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';

import { SiteMode, environment } from '../../api';
import { AppGridItem } from '../stores/app-list-page.store';
import { getDisplayNamesString } from '../utils';
import { AppReviewStatusTag } from './app-review-status-tag';

const CurrentVersionCell: FC<
    TableCellProps & {
        onOpenVersionDetails: (appGridItem: AppGridItem) => void;
    }
> = props => {
    const [{ formatDateFull }] = useDependencies(DateTimeUtils);
    const { currentScopesVersion } = props.dataItem as AppGridItem;
    const { version, dateTime } = currentScopesVersion;
    const handleOpenVersionDetails = () =>
        props.onOpenVersionDetails(props.dataItem as AppGridItem);
    return (
        <td>
            <Eyebrow>{`Version ${version}\u2002·\u2002${formatDateFull(dateTime)}`}</Eyebrow>
            <Link onClick={handleOpenVersionDetails} color="primary">
                View Version Details
            </Link>
        </td>
    );
};

const useCurrentVersionCell = (onOpenVersionDetails: (appGridItem: AppGridItem) => void) =>
    useCallback(
        (props: TableCellProps) => {
            return <CurrentVersionCell {...props} onOpenVersionDetails={onOpenVersionDetails} />;
        },
        [onOpenVersionDetails],
    );

const ReviewStatusCell: FC<TableCellProps> = props => {
    const { reviewStatus } = props.dataItem as AppGridItem;
    return (
        <td>
            <AppReviewStatusTag reviewStatus={reviewStatus} />
        </td>
    );
};

const ActionsCell: FC<TableCellProps> = props => {
    const dataItem = props.dataItem as AppGridItem;
    const history = useHistory();

    return (
        <td>
            <ButtonGroup>
                <Tooltip text={dataItem.deleted ? 'View' : 'Edit'} portal>
                    <Button
                        size="small"
                        iconName={dataItem.deleted ? 'info_outline' : 'edit'}
                        aria-label={dataItem.deleted ? 'View' : 'Edit'}
                        onClick={() => {
                            history.push(`/apps/${dataItem.id}`);
                        }}
                    />
                </Tooltip>
                <Tooltip text="App Connections" portal>
                    <Button
                        size="small"
                        iconName="link"
                        aria-label="App Connections"
                        onClick={() => {
                            history.push(`/apps/${dataItem.id}/clients`);
                        }}
                    />
                </Tooltip>
                {environment.site.mode === SiteMode.TenantApis && (
                    <Tooltip text="API Usage Report" portal>
                        <Button
                            size="small"
                            iconName="equalizer"
                            aria-label="API Usage Report"
                            onClick={() => {
                                history.push(`/apps/${dataItem.id}/api-usage-report`);
                            }}
                        />
                    </Tooltip>
                )}
                {!dataItem.deleted && dataItem.supportsWebhookEvents && (
                    <Tooltip text="Webhook Statuses" portal>
                        <Button
                            size="small"
                            iconName="perm_data_setting"
                            aria-label="Webhook Statuses"
                            onClick={() => {
                                history.push(`/apps/${dataItem.id}/webhook-mgmt`);
                            }}
                        />
                    </Tooltip>
                )}
            </ButtonGroup>
        </td>
    );
};

export interface AppSummaryTableProps {
    appGridItems: AppGridItem[];
    showApiCategories: boolean;
    showReviewStatus: boolean;
    noRecordsText?: string;
}

export const AppSummaryTable: FC<AppSummaryTableProps> = ({
    appGridItems,
    showApiCategories,
    showReviewStatus,
    noRecordsText,
}) => {
    const [appDetails, setAppDetails] = useState<AppGridItem | null>(null);

    const handleOpenVersionDetails = (appGridItem: AppGridItem) => {
        setAppDetails(appGridItem);
    };

    const handleCloseVersionDetails = () => {
        setAppDetails(null);
    };

    const currentVersionCell: FC<TableCellProps> = useCurrentVersionCell(handleOpenVersionDetails);

    return (
        <Fragment>
            <Table data={appGridItems} striped={false}>
                {noRecordsText && <TableNoRecords>{noRecordsText}</TableNoRecords>}
                <TableColumn field="appName" title="App Name" width={200} />
                {showApiCategories && (
                    <TableColumn field="apiGroupDisplayName" title="API Category" width={150} />
                )}
                <TableColumn title="Current Version" cell={currentVersionCell} />
                {showReviewStatus && (
                    <TableColumn title="App Review Status" width={240} cell={ReviewStatusCell} />
                )}
                <TableColumn
                    title="Actions"
                    width={environment.site.mode === SiteMode.TenantApis ? 200 : 160}
                    cell={ActionsCell}
                />
            </Table>
            {appDetails && (
                <Modal
                    open
                    size={Modal.Sizes.L}
                    title={`${appDetails.appName}\u2002·\u2002Version ${appDetails.currentScopesVersion.version}`}
                    onClose={handleCloseVersionDetails}
                    footer={
                        <Button primary onClick={handleCloseVersionDetails}>
                            Close
                        </Button>
                    }
                >
                    <Stack direction="column" spacing={2}>
                        <Divider alignContent="start">
                            <Eyebrow size="large" className="c-neutral-400 m-r-1">
                                APIs
                            </Eyebrow>
                        </Divider>
                        <Eyebrow>Scopes</Eyebrow>
                        <Stack.Item>
                            <ul className="m-y-0">
                                <li>
                                    {`Read: ${getDisplayNamesString(
                                        appDetails.currentScopesVersion.authScopes.filter(
                                            authScope => authScope.read,
                                        ),
                                    )}`}
                                </li>
                                <li className="m-t-2">
                                    {`Write: ${getDisplayNamesString(
                                        appDetails.currentScopesVersion.authScopes.filter(
                                            authScope => authScope.write,
                                        ),
                                    )}`}
                                </li>
                            </ul>
                        </Stack.Item>
                        {appDetails.currentScopesVersion.webhookEvents.length > 0 && (
                            <Fragment>
                                <Divider alignContent="start">
                                    <Eyebrow size="large" className="c-neutral-400 m-r-1">
                                        Webhooks
                                    </Eyebrow>
                                </Divider>
                                <Eyebrow>Events</Eyebrow>
                                <Stack.Item>
                                    <ul className="m-y-0">
                                        <li>
                                            {`Receive: ${getDisplayNamesString(
                                                appDetails.currentScopesVersion.webhookEvents,
                                            )}`}
                                        </li>
                                    </ul>
                                </Stack.Item>
                            </Fragment>
                        )}
                    </Stack>
                </Modal>
            )}
        </Fragment>
    );
};
